import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ROADMAP_DATA } from '../../data/constants';
import { Bot, Zap, Gem, Brain, X, ArrowRight, Layers, Share2, Cpu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const RoadmapSection = () => {
    const [selectedCard, setSelectedCard] = useState(null);
    const navigate = useNavigate();

    const visionCards = [
        {
            id: 1,
            icon: Bot,
            title: "Autonomous Lobster Execution",
            desc: "Agents don't just signal — they execute. Fully autonomous delta-neutral trading strategies run by AI on Layer 2.",
            color: "text-red-500",
            borderColor: "border-red-500/50",
            glow: "shadow-[0_0_50px_rgba(220,38,38,0.2)]"
        },
        {
            id: 2,
            icon: Share2,
            title: "Cross-Chain Liquidity",
            desc: "Unified liquidity pools across Solana, Base, and Arbitrum. Trade on any chain, settle instantly.",
            color: "text-primary",
            borderColor: "border-primary/50",
            glow: "shadow-[0_0_50px_rgba(255,77,77,0.2)]"
        },
        {
            id: 3,
            icon: Gem,
            title: "DAO Revenue Share",
            desc: "Holders of $PREDICT stake tokens to earn 40% of protocol fees generated by AI prediction volume.",
            color: "text-secondary",
            borderColor: "border-secondary/50",
            glow: "shadow-[0_0_50px_rgba(0,212,170,0.2)]"
        },
        {
            id: 4,
            icon: Brain,
            title: "Neural Market Sentiment",
            desc: "Real-time scanning of 500M+ data points (Video, Audio, Text) for hyper-accurate outcomes.",
            color: "text-secondary",
            borderColor: "border-secondary/50",
            glow: "shadow-[0_0_50px_rgba(0,212,170,0.2)]"
        }
    ];

    return (
        <section className="py-24 bg-[#1a1a1b] border-t border-white/5 relative">
            <div className="container mx-auto px-4">
                <div className="flex flex-col lg:flex-row gap-16">
                    {/* Left Column: Roadmap */}
                    <div className="lg:w-1/3">
                        <div className="sticky top-24">
                            <h2 className="text-2xl font-bold text-white mb-2 uppercase tracking-tight">Roadmap 2026</h2>
                            <p className="text-[#6a6a6a] text-sm mb-10">The path to decentralized intelligence.</p>

                            <div className="space-y-12 relative border-l border-white/10 pl-8">
                                {ROADMAP_DATA.map((item, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        className="relative"
                                    >
                                        <div className="absolute -left-[37px] top-1 w-4 h-4 rounded-full bg-[#1a1a1b] border-2 border-primary" />
                                        <div className="text-red-500 font-mono font-bold text-sm mb-1">{item.phase}</div>
                                        <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                        <ul className="space-y-1">
                                            {item.items.map((sub, j) => (
                                                <li key={j} className="text-sm text-muted-foreground">• {sub}</li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Vision Cards */}
                    <div className="lg:w-2/3">
                        <h2 className="text-2xl font-bold text-white mb-10 uppercase tracking-tight">Future Vision</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {visionCards.map((card) => (
                                <motion.div
                                    key={card.id}
                                    layoutId={`card-${card.id}`}
                                    onClick={() => setSelectedCard(card)}
                                    whileHover={{ y: -5, scale: 1.01 }}
                                    className={`p-8 rounded bg-[#242425] border border-white/5 hover:border-primary transition-all cursor-pointer group relative overflow-hidden`}
                                >
                                    <div className={`absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity ${card.color}`}>
                                        <card.icon size={120} />
                                    </div>
                                    <div className={`text-4xl mb-4 ${card.color}`}><card.icon /></div>
                                    <h3 className="text-xl font-bold text-white mb-3">{card.title}</h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                                        {card.desc}
                                    </p>
                                    <div className={`text-xs font-bold uppercase tracking-widest flex items-center gap-2 ${card.color}`}>
                                        View Simulation <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Interactive Modal */}
            <AnimatePresence>
                {selectedCard && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/90 backdrop-blur-md">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedCard(null)}
                            className="absolute inset-0"
                        />
                        <motion.div
                            layoutId={`card-${selectedCard.id}`}
                            className="bg-[#0f0f0f] w-full max-w-4xl rounded-3xl overflow-hidden border border-white/10 relative z-10 grid md:grid-cols-2 shadow-2xl"
                        >
                            <button
                                onClick={(e) => { e.stopPropagation(); setSelectedCard(null); }}
                                className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-white/10 transition-colors z-20 text-white"
                            >
                                <X size={20} />
                            </button>

                            {/* Enhanced Visual Animation Area */}
                            <div className="h-80 md:h-auto bg-gradient-to-br from-black to-[#050505] relative flex items-center justify-center overflow-hidden border-b md:border-b-0 md:border-r border-white/10">
                                <EnhancedVisualAnimation id={selectedCard.id} color={selectedCard.color} />
                            </div>

                            {/* Content Area */}
                            <div className="p-10 flex flex-col justify-center">
                                <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-6 ${selectedCard.color}`}>
                                    <selectedCard.icon size={24} />
                                </div>
                                <h3 className="text-3xl font-black text-white mb-4 leading-tight">{selectedCard.title}</h3>
                                <p className="text-gray-400 leading-relaxed mb-8 text-lg">
                                    {selectedCard.desc}
                                    <br /><br />
                                    This module is part of the "Grand Unification" update, connecting off-chain data with on-chain execution.
                                </p>
                                <button
                                    onClick={() => navigate('/beta-simulation')}
                                    className={`w-full py-4 rounded-xl font-bold text-black text-lg ${selectedCard.color.replace('text', 'bg').replace('-400', '-500')} hover:opacity-90 transition-all shadow-lg hover:scale-[1.02]`}
                                >
                                    Access Unified Beta Simulation
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </section>
    );
};

// Significantly Enhanced Animations
const EnhancedVisualAnimation = ({ id, color }) => {
    const colorHex = color.includes('primary') || color.includes('red') ? '#ff4d4d'
        : '#00d4aa'; // secondary/teal

    if (id === 1) { // Autonomous Lobster
        return (
            <div className="relative w-full h-full flex items-center justify-center">
                {/* Core */}
                <motion.div
                    animate={{ scale: [1, 1.1, 1], boxShadow: [`0 0 20px ${colorHex}50`, `0 0 60px ${colorHex}80`, `0 0 20px ${colorHex}50`] }}
                    className={`w-20 h-20 rounded-full border-2 ${color.replace('text', 'border')} bg-black flex items-center justify-center z-10 relative`}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <Bot size={40} className={color} />
                </motion.div>

                {/* Orbital Rings - Inner */}
                <motion.div
                    className={`absolute w-40 h-40 border-2 border-dashed ${color.replace('text', 'border')} rounded-full opacity-30`}
                    animate={{ rotate: 360, scale: [0.9, 1, 0.9] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                />
                {/* Orbital Rings - Outer */}
                <motion.div
                    className={`absolute w-60 h-60 border border-dotted ${color.replace('text', 'border')} rounded-full opacity-20`}
                    animate={{ rotate: -360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />

                {/* Satellites */}
                {[0, 1, 2, 3].map(i => (
                    <motion.div
                        key={i}
                        className="absolute w-full h-full"
                        animate={{ rotate: i * 90 + 360 }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear", delay: i }}
                    >
                        <div className={`absolute top-1/4 left-1/2 w-3 h-3 rounded-full ${color.replace('text', 'bg')} shadow-[0_0_15px_${colorHex}]`} />
                    </motion.div>
                ))}
            </div>
        )
    }

    if (id === 2) { // Cross-Chain Liquidity
        return (
            <div className="relative w-full h-full flex flex-col items-center justify-center gap-12">
                <div className="flex gap-20">
                    {['SOL', 'ETH'].map((chain, i) => (
                        <div key={i} className="relative">
                            <motion.div
                                className={`w-16 h-16 rounded-2xl border-2 ${color.replace('text', 'border')} bg-black flex items-center justify-center font-bold text-white z-10 relative`}
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 2, delay: i, repeat: Infinity }}
                            >
                                {chain}
                            </motion.div>
                            <div className={`absolute inset-0 ${color.replace('text', 'bg')} blur-xl opacity-20`} />
                        </div>
                    ))}
                </div>

                {/* Flow Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                    <motion.path
                        d="M 160 140 Q 250 200 340 140"
                        fill="none"
                        stroke={colorHex}
                        strokeWidth="2"
                        strokeDasharray="10 10"
                        animate={{ strokeDashoffset: [0, -20] }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.path
                        d="M 250 260 L 250 200"
                        fill="none"
                        stroke={colorHex}
                        strokeWidth="2"
                        strokeDasharray="10 10"
                        animate={{ strokeDashoffset: [0, -20] }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                </svg>

                <div className="relative">
                    <motion.div
                        className={`w-20 h-20 rounded-full border-4 ${color.replace('text', 'border')} bg-black flex items-center justify-center z-10 relative shadow-[0_0_50px_${colorHex}40]`}
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    >
                        <span className="text-white font-black text-xs">PREDICT</span>
                    </motion.div>
                </div>
            </div>
        )
    }

    if (id === 3) { // Revenue Share
        return (
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                {/* Rising Coins */}
                {[0, 1, 2, 3, 4].map(i => (
                    <motion.div
                        key={i}
                        initial={{ y: 200, opacity: 0, rotate: 0 }}
                        animate={{ y: -200, opacity: [0, 1, 1, 0], rotate: 360 }}
                        transition={{ duration: 4, delay: i * 0.8, repeat: Infinity, ease: "linear" }}
                        className={`absolute flex items-center justify-center`}
                        style={{ left: `${20 + (i * 15)}%` }}
                    >
                        <div className={`w-8 h-8 rounded-full border-2 ${color.replace('text', 'border')} flex items-center justify-center text-[10px] font-bold text-white bg-black`}>$</div>
                    </motion.div>
                ))}

                {/* Central Pot */}
                <div className="absolute bottom-10 w-32 h-16 rounded-b-full border-b-4 border-l-4 border-r-4 border-white/20 bg-white/5 backdrop-blur-md z-10" />
                <div className={`absolute bottom-14 w-full text-center font-black text-2xl ${color} z-20`}>
                    <motion.span animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
                        40% APY
                    </motion.span>
                </div>
            </div>
        )
    }

    // Neural Market (Matrix style)
    return (
        <div className="relative w-full h-full flex items-center justify-center bg-black">
            <div className="grid grid-cols-8 gap-4 w-full h-full opacity-40">
                {Array.from({ length: 48 }).map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{ opacity: [0.1, 1, 0.1], scale: [1, 1.5, 1] }}
                        transition={{ duration: Math.random() * 2 + 1, repeat: Infinity }}
                        className={`w-1 h-1 rounded-full ${color.replace('text', 'bg')}`}
                    />
                ))}
            </div>

            {/* Scanning Bar */}
            <motion.div
                className={`absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-transparent to-${color.replace('text-', '')}/20`}
                animate={{ top: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />

            <div className="absolute inset-0 flex items-center justify-center">
                <div className={`text-4xl font-black ${color} tracking-widest mix-blend-screen bg-black px-4 py-2 border ${color.replace('text', 'border')}`}>
                    AI SCANNED
                </div>
            </div>
        </div>
    )
};
